// API client for the broker portal. Talks to api-gateway `/api/*` on the
// same origin (nginx proxies it), so the session cookie stays
// first-party:
//
//   /api/auth/*     the ordinary user session (login, MFA, me, logout)
//   /api/broker/*   broker-service's staff surface, forwarded by the
//                   gateway with the session user as X-User-ID; broker-
//                   service re-authorizes every call (404 outside the
//                   caller's scope, 403 BROKER_000103 when the role lacks
//                   the capability)
//   /api/currencies the public currency presentation registry
//
// The portal never talks to the exchange's admin surface (/admin/*).
//
// Session: cookie + CSRF replay. Login (and /api/auth/me) hands back a
// `csrf_token`, stored under the portal's OWN key so signing out here
// never disturbs a web-client session in another tab, and replayed as
// X-CSRF-Token on every non-GET.
//
// Response headers are part of the answer: a broker mutation names the
// audit events it wrote in X-Broker-Audit-Event-Ids (plan D65) and every
// response carries the correlation id in X-Request-ID. `request()`
// returns them; `api()` is the body-only shorthand.
//
// Relative, extensioned imports: this module runs under
// `node --experimental-strip-types --test`, which has no alias table.
import { recordApiCall } from "./telemetry.ts";

export const CSRF_KEY = "sm_broker_csrf";
export const AUDIT_HEADER = "X-Broker-Audit-Event-Ids";
export const REQUEST_ID_HEADER = "X-Request-ID";
export const IDEMPOTENCY_HEADER = "Idempotency-Key";

/** A failed call. `code` is the server's error code (or a transport one). */
export class APIError extends Error {
  status: number;
  code: string;
  body: unknown;
  headers: Headers;
  constructor(status: number, code: string, message: string, body: unknown, headers?: Headers) {
    super(message);
    this.name = "APIError";
    this.status = status;
    this.code = code;
    this.body = body;
    this.headers = headers ?? new Headers();
  }

  /** The correlation id the gateway stamped, for support. */
  get requestId(): string | undefined {
    const fromHeader = this.headers.get(REQUEST_ID_HEADER);
    if (fromHeader) return fromHeader;
    const env = (this.body as { error?: { request_id?: string } } | null)?.error;
    return env?.request_id || undefined;
  }

  /** The broker envelope's details map ({} when absent). */
  get details(): Record<string, string> {
    const env = (this.body as { error?: { details?: Record<string, string> } } | null)?.error;
    return env?.details ?? {};
  }
}

/** Transport failure codes (no HTTP answer at all). */
export const NETWORK_ERROR_CODE = "NETWORK_ERROR";

// --- CSRF storage ------------------------------------------------------------

interface KeyValueStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

function browserStorage(): KeyValueStorage | null {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch {
    return null;
  }
}

export function getCSRF(storage: KeyValueStorage | null = browserStorage()): string | null {
  return storage?.getItem(CSRF_KEY) ?? null;
}

export function setCSRF(token: string | null, storage: KeyValueStorage | null = browserStorage()): void {
  if (!storage) return;
  if (token) storage.setItem(CSRF_KEY, token);
  else storage.removeItem(CSRF_KEY);
}

// --- auth-failure hook --------------------------------------------------------
//
// A 401 on ANY call means the session is gone (expired, signed out in
// another tab, revoked). The shell registers one handler that clears the
// query cache and the CSRF token and sends the user to /login
// (lib/session.ts); the client only reports it.

type AuthFailureHandler = (err: APIError) => void;
let authFailureHandler: AuthFailureHandler | null = null;

export function setAuthFailureHandler(fn: AuthFailureHandler | null): void {
  authFailureHandler = fn;
}

// --- requests -----------------------------------------------------------------

export interface ApiOptions {
  method?: "GET" | "POST" | "PATCH" | "DELETE" | "PUT";
  body?: unknown;
  /**
   * A multipart body (SM-978's image upload), sent as-is: the browser sets
   * the Content-Type and its boundary. Exclusive with `body`.
   */
  form?: FormData;
  signal?: AbortSignal;
  skipCSRF?: boolean;
  /** Idempotency-Key for a money command (lib/idempotency.ts). */
  idempotencyKey?: string;
  /**
   * The login flow reads 428 (MFA challenge) as an answer, not a failure.
   */
  expectMFAChallenge?: boolean;
  /**
   * Do not report a 401 to the auth-failure handler: /api/auth/me and
   * the login calls treat "no session" as an ordinary answer.
   */
  anonymousOK?: boolean;
  /** Test seam; defaults to the global fetch. */
  fetchImpl?: typeof fetch;
}

export interface ApiResponse<T> {
  data: T;
  status: number;
  headers: Headers;
}

export async function request<T = unknown>(path: string, opts: ApiOptions = {}): Promise<ApiResponse<T>> {
  const method = opts.method ?? "GET";
  const headers: Record<string, string> = { Accept: "application/json" };
  if (opts.body !== undefined) headers["Content-Type"] = "application/json";
  if (!opts.skipCSRF && method !== "GET") {
    const tok = getCSRF();
    if (tok) headers["X-CSRF-Token"] = tok;
  }
  if (opts.idempotencyKey) headers[IDEMPOTENCY_HEADER] = opts.idempotencyKey;

  const doFetch = opts.fetchImpl ?? fetch;
  const started = Date.now();
  let res: Response;
  try {
    res = await doFetch(path, {
      method,
      headers,
      credentials: "include",
      body: opts.form ?? (opts.body !== undefined ? JSON.stringify(opts.body) : undefined),
      signal: opts.signal,
    });
  } catch (err) {
    if ((err as { name?: string })?.name === "AbortError") throw err;
    recordApiCall({ method, path, status: 0, latencyMs: Date.now() - started });
    throw new APIError(0, NETWORK_ERROR_CODE, (err as Error)?.message || "network error", null);
  }
  recordApiCall({
    method,
    path,
    status: res.status,
    latencyMs: Date.now() - started,
    correlationId: res.headers.get(REQUEST_ID_HEADER) ?? undefined,
  });

  if (res.status === 204) return { data: undefined as T, status: res.status, headers: res.headers };
  let json: unknown = null;
  const text = await res.text();
  if (text) {
    try {
      json = JSON.parse(text);
    } catch {
      json = text;
    }
  }
  if (res.status === 428 && opts.expectMFAChallenge) {
    return { data: json as T, status: res.status, headers: res.headers };
  }
  if (!res.ok) {
    const env = (json as { error?: { code?: string; message?: string } } | null)?.error;
    const err = new APIError(res.status, env?.code ?? `HTTP_${res.status}`, env?.message ?? `HTTP ${res.status}`, json, res.headers);
    if (res.status === 401 && !opts.anonymousOK) authFailureHandler?.(err);
    throw err;
  }
  return { data: json as T, status: res.status, headers: res.headers };
}

export async function api<T = unknown>(path: string, opts: ApiOptions = {}): Promise<T> {
  return (await request<T>(path, opts)).data;
}

/**
 * qs serializes a flat object into a leading-? query string. Keys with
 * undefined / null / "" values are omitted.
 *
 * `broker_account_id` is REFUSED: api-gateway strips that key from every
 * /api/* query and body (SM-693 anti-spoofing), so a query that carried it
 * would silently lose its filter. One account's reads travel in the path
 * (…/accounts/{broker_account_id}/…).
 */
export function qs(params: Record<string, string | number | boolean | undefined | null>): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (k === "broker_account_id") throw new Error("broker_account_id never travels in a query (SM-693)");
    if (v === undefined || v === "" || v === null) continue;
    usp.set(k, String(v));
  }
  const s = usp.toString();
  return s ? `?${s}` : "";
}

/** A path segment, escaped (ids are server-generated but never trusted). */
export function seg(id: string): string {
  return encodeURIComponent(id);
}

/**
 * The audit event ids a mutation wrote (X-Broker-Audit-Event-Ids,
 * comma-separated in write order). [] when the header is absent.
 */
export function auditRefs(headers: Headers | null | undefined): string[] {
  const raw = headers?.get(AUDIT_HEADER);
  if (!raw) return [];
  return raw
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}
